import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import OneSignalWebService from '../services/OneSignalWebService';
import { toast } from 'react-toastify';

export default function WebPushButton({ user }) {
  const [status, setStatus] = useState(() => ({
    supported: OneSignalWebService.isSupported(),
    permission: typeof Notification !== 'undefined' && Notification.permission === 'granted',
    optedIn: false
  }));
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [showIosInstructions, setShowIosInstructions] = useState(false);
  const environment = OneSignalWebService.getEnvironment();

  useEffect(() => {
    let active = true;
    OneSignalWebService.getStatus(user)
      .then((nextStatus) => active && setStatus(nextStatus))
      .catch((error) => console.warn('[OneSignal Web] inicializacao:', error.message))
      .finally(() => active && setInitializing(false));
    return () => { active = false; };
  }, [user]);

  const enableNotifications = async () => {
    if (environment.requiresInstallation) {
      setShowIosInstructions(true);
      return;
    }
    if (!OneSignalWebService.isSupported()) {
      toast.info('Este aparelho ou navegador nao oferece notificacoes Web Push.');
      return;
    }
    try {
      setLoading(true);
      const nextStatus = await OneSignalWebService.requestPermission(user);
      setStatus((current) => ({ ...current, ...nextStatus }));
      if (nextStatus.permission && nextStatus.optedIn) {
        toast.success('Notificacoes ativadas neste aparelho.');
      } else {
        toast.warning('Permita as notificacoes nas configuracoes do navegador.');
      }
    } catch (error) {
      if (error.code === 'IOS_INSTALL_REQUIRED') {
        setShowIosInstructions(true);
        return;
      }
      toast.error(error.message || 'Nao foi possivel ativar as notificacoes');
    } finally {
      setLoading(false);
    }
  };

  const enabled = status.permission && status.optedIn;
  return (
    <>
      <Tooltip title={enabled ? 'Notificacoes ativadas' : initializing ? 'Carregando notificacoes...' : 'Ativar notificacoes'}>
        <span>
          <IconButton color="inherit" onClick={enableNotifications} disabled={loading || (initializing && !environment.requiresInstallation)}>
            {(loading || (initializing && !environment.requiresInstallation)) ? <CircularProgress size={22} color="inherit" /> : (
              <Badge variant="dot" color={enabled ? 'success' : 'warning'}>
                {enabled ? <NotificationsActiveIcon /> : <NotificationsOffIcon />}
              </Badge>
            )}
          </IconButton>
        </span>
      </Tooltip>

      <Dialog open={showIosInstructions} onClose={() => setShowIosInstructions(false)} fullWidth maxWidth="xs">
        <DialogTitle>Ativar notificacoes no iPhone</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              No iPhone, Safari e Chrome so recebem notificacoes quando o All Delivery esta instalado na Tela de Inicio.
            </Alert>
            <Typography>1. Abra o site no Safari.</Typography>
            <Typography>2. Toque em Compartilhar (quadrado com seta para cima).</Typography>
            <Typography>3. Toque em <strong>Adicionar a Tela de Inicio</strong>.</Typography>
            <Typography>4. Feche o navegador, abra o All Delivery pelo novo icone e toque novamente no sino.</Typography>
            <Typography variant="caption" color="text.secondary">
              Requer iOS 16.4 ou mais recente. Depois de permitir, confira tambem Ajustes &gt; Notificacoes &gt; All Delivery.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowIosInstructions(false)} variant="contained">Entendi</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
